<template>
  <div class="gm-panel formula-builder">
    <div class="section-header">
      <h4 class="gm-section-title">{{ title || 'Formula Configuration' }}</h4>
    </div>

    <!-- Base Value -->
    <div class="gm-field">
      <label class="gm-label">Base Value</label>
      <input
        v-model.number="formula.base"
        type="number"
        class="gm-input"
        placeholder="0"
      />
    </div>

    <!-- Stat Requirements -->
    <div class="subsection">
      <div class="subsection-header">
        <h5 class="gm-section-title">Stat Requirements</h5>
        <button type="button" @click="addRequirement" class="gm-btn gm-btn--sm">
          Add Requirement
        </button>
      </div>

      <div v-if="formula.requires.length === 0" class="gm-empty">
        <p class="gm-empty__hint">No stat requirements. Click "Add Requirement" to add requirements.</p>
      </div>

      <div v-for="(req, index) in formula.requires" :key="index" class="gm-panel requirement-item">
        <div class="requirement-fields">
          <div class="gm-field">
            <label class="gm-label">Stat</label>
            <select v-model="req.stat" class="gm-select">
              <option :value="CharacterStatEnum.PhysicalStrength">Physical Strength</option>
              <option :value="CharacterStatEnum.MentalStrength">Mental Strength</option>
              <option :value="CharacterStatEnum.FlowResonance">Flow Resonance</option>
              <option :value="CharacterStatEnum.Concentration">Concentration</option>
              <option :value="CharacterStatEnum.FlowManipulation">Flow Manipulation</option>
              <option :value="CharacterStatEnum.FlowConnection">Flow Connection</option>
              <option :value="CharacterStatEnum.Knowledge">Knowledge</option>
              <option :value="CharacterStatEnum.Speed">Speed</option>
              <option :value="CharacterStatEnum.Luck">Luck</option>
              <option :value="CharacterStatEnum.Charisma">Charisma</option>
            </select>
          </div>

          <div class="gm-field">
            <label class="gm-label">Required Value</label>
            <input
              v-model.number="req.value"
              type="number"
              min="0"
              class="gm-input"
              placeholder="0"
            />
          </div>

          <button type="button" @click="removeRequirement(index)" class="gm-btn gm-btn--sm gm-btn--danger">
            Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Scaling Factors -->
    <div class="subsection">
      <div class="subsection-header">
        <h5 class="gm-section-title">Scaling Factors</h5>
        <button type="button" @click="addScaling" class="gm-btn gm-btn--sm">
          Add Scaling
        </button>
      </div>

      <div v-if="formula.scaling.length === 0" class="gm-empty">
        <p class="gm-empty__hint">No scaling factors. Click "Add Scaling" to add stat scaling.</p>
      </div>

      <div v-for="(scaling, index) in formula.scaling" :key="index" class="gm-panel scaling-item">
        <div class="scaling-fields">
          <div class="gm-field">
            <label class="gm-label">Stat</label>
            <select v-model="scaling.stat" class="gm-select">
              <option :value="CharacterStatEnum.PhysicalStrength">Physical Strength</option>
              <option :value="CharacterStatEnum.MentalStrength">Mental Strength</option>
              <option :value="CharacterStatEnum.FlowResonance">Flow Resonance</option>
              <option :value="CharacterStatEnum.Concentration">Concentration</option>
              <option :value="CharacterStatEnum.FlowManipulation">Flow Manipulation</option>
              <option :value="CharacterStatEnum.FlowConnection">Flow Connection</option>
              <option :value="CharacterStatEnum.Knowledge">Knowledge</option>
              <option :value="CharacterStatEnum.Speed">Speed</option>
              <option :value="CharacterStatEnum.Luck">Luck</option>
              <option :value="CharacterStatEnum.Charisma">Charisma</option>
            </select>
          </div>

          <div class="gm-field">
            <label class="gm-label">Scaling Value</label>
            <input
              v-model.number="scaling.value"
              type="number"
              step="0.1"
              class="gm-input"
              placeholder="0.0"
            />
          </div>

          <button type="button" @click="removeScaling(index)" class="gm-btn gm-btn--sm gm-btn--danger">
            Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Efficiency Settings -->
    <div class="efficiency-section">
      <h5 class="gm-section-title">Efficiency Limits</h5>
      <div class="efficiency-fields">
        <div class="gm-field">
          <label class="gm-label">Min Efficiency</label>
          <input
            v-model.number="formula.min_efficiency"
            type="number"
            step="0.1"
            class="gm-input"
            placeholder="Optional"
          />
        </div>

        <div class="gm-field">
          <label class="gm-label">Max Efficiency</label>
          <input
            v-model.number="formula.max_efficiency"
            type="number"
            step="0.1"
            class="gm-input"
            placeholder="Optional"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { CharacterStatEnum, type FormulaRequest, type StatRequirementRequest, type ScalingRequest } from '@/api/dx-backend'

// Props
interface Props {
  modelValue: FormulaRequest
  title?: string
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: FormulaRequest]
}>()

// Reactive state
const formula = ref<FormulaRequest>({
  base: props.modelValue.base || 0,
  requires: props.modelValue.requires || [],
  scaling: props.modelValue.scaling || [],
  min_efficiency: props.modelValue.min_efficiency || null,
  max_efficiency: props.modelValue.max_efficiency || null
})

// Methods
const addRequirement = () => {
  formula.value.requires = formula.value.requires || []
  formula.value.requires.push({
    stat: CharacterStatEnum.PhysicalStrength,
    value: 0
  })
  emitUpdate()
}

const removeRequirement = (index: number) => {
  formula.value.requires?.splice(index, 1)
  emitUpdate()
}

const addScaling = () => {
  formula.value.scaling = formula.value.scaling || []
  formula.value.scaling.push({
    stat: CharacterStatEnum.PhysicalStrength,
    value: 0
  })
  emitUpdate()
}

const removeScaling = (index: number) => {
  formula.value.scaling?.splice(index, 1)
  emitUpdate()
}

const emitUpdate = () => {
  emit('update:modelValue', { ...formula.value })
}

// Watch for external changes
watch(() => props.modelValue, (newValue) => {
  formula.value = {
    base: newValue.base || 0,
    requires: newValue.requires || [],
    scaling: newValue.scaling || [],
    min_efficiency: newValue.min_efficiency || null,
    max_efficiency: newValue.max_efficiency || null
  }
}, { deep: true })

// Watch for internal changes
watch(formula, () => {
  emitUpdate()
}, { deep: true })
</script>

<style scoped>
.formula-builder {
  margin-bottom: var(--gm-gap);
}

.section-header {
  margin-bottom: var(--gm-gap);
}

.subsection {
  margin-bottom: var(--gm-gap-lg);
}

.subsection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
}

.efficiency-section .gm-section-title {
  margin-bottom: var(--gm-gap-sm);
}

.requirement-item,
.scaling-item {
  margin-bottom: var(--gm-gap-sm);
}

.requirement-fields,
.scaling-fields,
.efficiency-fields {
  display: flex;
  gap: var(--gm-gap);
  align-items: end;
}

.gm-field {
  flex: 1;
}
</style>
